import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Skeleton, TextInput } from '@mantine/core';
import { CornerUpLeft, CornerUpRight, Loader } from 'tabler-icons-react';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import { usePaginationFilter } from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { singularPluralFormat } from '../../../helpers/format';
import AppImage from '../../common/AppImage';

const AdminStockContentAssignedStoreList = ({ pkEcomStockContent }) => {
  const hasFetched = useRef(false);
  const { state, fetchPaginatedEcomStockContentStores } = useContext(
    AdminContext
  );
  const {
    pageIndex,
    totalCount,
    totalPages,
    data,
    filter,
    loading: dataLoading,
    onPageChange,
    onFilterChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      status: ''
    },
    (newFilter) => {
      fetchPaginatedEcomStockContentStores(
        pkEcomStockContent,
        {
          ...newFilter,
          pageSize: 10
        },
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedEcomStockContentStores
  );
  const hasAlreadyFetched =
    state.paginatedEcomStockContentStores.filter?.pkEcomStockContent ===
    pkEcomStockContent;
  const loading = !!pkEcomStockContent && (dataLoading || !hasAlreadyFetched);
  const storeData = hasAlreadyFetched ? data : [];

  useEffect(() => {
    if (pkEcomStockContent && !hasAlreadyFetched) {
      onRefresh();
      hasFetched.current = true;
    }
  }, [pkEcomStockContent]);

  return (
    <AppStack style={{ gap: 0 }}>
      <AppFlexbox
        style={{
          padding: 5,
          gap: 5,
          alignItems: 'end',
          justifyContent: 'space-between'
        }}
      >
        <TextInput
          onChange={(e) => {
            onFilterChange({ ...filter, search: e.currentTarget.value });
          }}
          placeholder="Search by name..."
          value={filter.search || ''}
        />

        <AppStack style={{ gap: 0, alignItems: 'flex-end' }}>
          {loading ? (
            <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
              <Loader color="grey" size={15} />
              <AppText style={{ color: 'grey', fontSize: 14 }}>results</AppText>
            </AppFlexbox>
          ) : (
            <AppText
              style={{ color: 'grey', fontSize: 14, textWrap: 'balance' }}
            >
              Showing {data.length} of{' '}
              {singularPluralFormat(totalCount, 'result', 'results')}
            </AppText>
          )}

          <AppFlexbox style={{ gap: 8 }}>
            <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
              {loading ? (
                <Loader color="grey" size={18} />
              ) : (
                <AppText style={{ fontSize: 14, color: '#666' }}>
                  ({pageIndex}/{totalPages})
                </AppText>
              )}

              <Button.Group>
                <Button
                  disabled={pageIndex <= 1 || loading}
                  onClick={() => onPageChange(pageIndex - 1)}
                  size="compact-md"
                  variant="default"
                >
                  <CornerUpLeft size={18} />
                </Button>
                <Button
                  disabled={pageIndex >= totalPages || loading}
                  onClick={() => onPageChange(pageIndex + 1)}
                  size="compact-md"
                  variant="default"
                >
                  <CornerUpRight size={18} />
                </Button>
              </Button.Group>
            </AppFlexbox>
          </AppFlexbox>
        </AppStack>
      </AppFlexbox>

      <Divider />

      <AppStack style={{ gap: 0 }}>
        {loading ? (
          Array.from(Array(25)).map((x, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {index > 0 && <Divider />}

              <AppFlexbox
                style={{
                  padding: 8,
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                  <AppStack style={{ height: 50, width: 50 }}>
                    <Skeleton height={50} width={50} />
                  </AppStack>

                  <AppStack style={{ gap: 5 }}>
                    <Skeleton height={14} width={150} />
                    <Skeleton height={14} width={100} />
                  </AppStack>
                </AppFlexbox>
                <AppStack style={{ gap: 0, alignItems: 'flex-end' }}>
                  <Skeleton height={14} width={80} />
                </AppStack>
              </AppFlexbox>
            </React.Fragment>
          ))
        ) : storeData.length === 0 ? (
          <AppStack style={{ padding: 16 }}>
            <AppText
              style={{
                fontSize: 14,
                color: '#666',
                textAlign: 'center',
                fontStyle: 'italic'
              }}
            >
              No stores assigned yet.
            </AppText>
          </AppStack>
        ) : (
          storeData.map(({ ecomStore }, index) => (
            <React.Fragment key={ecomStore.pkEcomStore}>
              {index > 0 && <Divider />}

              <AppFlexbox
                style={{
                  padding: 8,
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                  <AppStack style={{ height: 50, width: 50 }}>
                    <AppImage
                      height={50}
                      src={ecomStore.logoImageUrl}
                      width={50}
                    />
                  </AppStack>

                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      {ecomStore.name}
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      {ecomStore.supportEmail}
                    </AppText>
                  </AppStack>
                </AppFlexbox>
              </AppFlexbox>
            </React.Fragment>
          ))
        )}
      </AppStack>
    </AppStack>
  );
};

AdminStockContentAssignedStoreList.propTypes = {
  pkEcomStockContent: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default AdminStockContentAssignedStoreList;
